import {
  deriveAuctionPhaseKindFromRow,
  isAuctionAwaitingCloseFromRow,
  type AuctionPhaseKindRow,
} from '../../isomorphic/lifecycle/derive-auction-phase-kind-from-row';

// BL-1065: the single source of truth for which auction lifecycle states an
// inventory item can still be attached to. Shared by the client Auction picker
// (`useLotEligibleAuctions`) and the server-side backstop (`PATCH /api/lots/[id]`)
// so the two can't drift. Terminal / late states (Post-Sale, Closed, Deleted) are
// excluded — a lot can't be added to an auction that has already ended.

/** Lot-eligible auction status labels, in lifecycle order. */
// BL-1285: ADR-023 phase tokens. `paused` is included — a paused auction can
// still take lots (pause halts bidding, not cataloguing); before this card it
// derived the legacy `Active` label and was eligible.
//
// BL-1291 AC 4: `accepting-pre-bids` belongs here for the same reason. It is a
// PRE-open rung sitting between `ready` and `active`, and both of its neighbours
// take lots — an auction collecting advance bids is still being catalogued. It
// was missed when the rung landed because this list enumerates phases by name, so
// the new rung didn't shadow anything here; it simply wasn't a member, and the
// failure was a 400 on the Add-lot door rather than anything louder. Adding a lot
// to a pre-bidding auction does NOT start that lot pre-bidding — the lot needs its
// own `pre_bidding_at` stamp, which only an explicit operator open writes (AC 4's
// actual subject, and why this list can widen without widening the pre-bid window.)
export const LOT_ELIGIBLE_AUCTION_STATUS_LABELS = [
  'draft',
  'ready',
  'accepting-pre-bids',
  'active',
  'paused',
] as const;

/** Membership-test set of {@link LOT_ELIGIBLE_AUCTION_STATUS_LABELS}. */
export const LOT_ELIGIBLE_AUCTION_STATUSES: ReadonlySet<string> = new Set(
  LOT_ELIGIBLE_AUCTION_STATUS_LABELS,
);

/**
 * BL-1909 — can this auction take a lot RIGHT NOW? The one answer the write
 * guard (`authorizeAuctionTarget`'s lifecycle half) and the Assign / Move
 * pickers' list both read, so a picker can never offer an auction the write
 * then refuses.
 *
 * Two clauses, both load-bearing:
 * - the derived phase is on {@link LOT_ELIGIBLE_AUCTION_STATUSES};
 * - the sale has not already FINISHED (BL-1516): an `active` auction whose
 *   every lot is terminal and past its close is seconds from being stamped
 *   closed, and a lot placed there would be stranded.
 *
 * Pure (ADR 010 — Isomorphic Services). Fed a row without `lotsOpenCount` /
 * `lotsMaxCloseTime` the second clause would quietly answer "not finished" —
 * select the row through `auctionPhaseKindColumns`, never by hand.
 *
 * @param row - the auction's decision stamps and lot counts.
 * @param now - clock for the past-close test; defaults to `new Date()`.
 * @returns true when a lot may be placed in the auction.
 */
export function auctionAcceptsLotsFromRow(row: AuctionPhaseKindRow, now: Date = new Date()): boolean {
  if (isAuctionAwaitingCloseFromRow(row, now)) return false;
  return LOT_ELIGIBLE_AUCTION_STATUSES.has(deriveAuctionPhaseKindFromRow(row));
}

/**
 * BL-2146 (PRJ-75 sweep P3, AC 8) — the one sentence every door answers when
 * {@link auctionAcceptsLotsFromRow} (or its mid-write BL-2476 re-check) says
 * no: the single-lot PATCH, the move/assign write guard
 * (`authorizeAuctionTarget`), `LotPlaceInAuctionService`, and the bulk
 * `POST /api/lots/batch`. Four copies of this literal used to drift
 * independently; this is the one place that can.
 */
export const TARGET_AUCTION_NOT_ACCEPTING_LOTS_MESSAGE = 'Selected auction is no longer accepting new lots';
